import { inject, Injectable, signal } from '@angular/core'
import {
	AbstractNotification,
	AccountDeletingNotification,
	ApplicationUpdateRequiredNotification,
	CurrencyMemoryKeyUpdateNotification,
	CurrencyTicketUpdateNotification,
	DailyCreditNotification,
	NotEnoughKeyNotification,
	NotEnoughTicketNotification,
	NotificationType,
	PlayerNotificationsUpdateNotification,
	SecretSceneEndedNotification,
	TosAgreementExpiredNotification,
	TosAgreementRequiredNotification,
} from '@mos/api/notification/dto-client'
import { isServer } from '@mos/client/shared/utils'
import { SpotlightConfig } from '@mos/client/web/shop/feature/components/spotlight/spotlight-config'
import { TutorialService } from '@mos/client/web/tutorial/tutorial.service'
import { SECRET_SCENE_TUTORIAL } from '@mos/client/web/tutorial/tutorials/secret-scene-tutorial.component'
import { AuthRepository } from '@mos/shared/auth'
import { filter, Observable, Subject } from 'rxjs'

@Injectable({ providedIn: 'root' })
export class ApiNotificationService {
	private readonly authRepository = inject(AuthRepository)
	private readonly tutorialService = inject(TutorialService)

	showApplicationUpdate = signal(false)

	showAccountDeletionModal = signal(false)
	readonly accountDeletionEffectiveDate = signal<Date>(new Date())

	showDailyIncomeModal = signal(false)
	readonly dailyIncome = signal<SpotlightConfig>({})

	showBankruptcy = signal(false)
	showBankruptcyKeys = signal(false)
	showBankruptcyGift = signal(false)

	showCguUpdate = signal(false)

	readonly cguInitial = signal(false)

	private notifications = new Subject<AbstractNotification>()

	on<T extends AbstractNotification>(notificationId: NotificationType): Observable<T> {
		return this.notifications.pipe(filter((notification): notification is T => notification.id === notificationId))
	}

	dispatch(notification: AbstractNotification) {
		this.notifications.next(notification)
	}

	constructor() {
		if (isServer) return

		this.on<ApplicationUpdateRequiredNotification>(NotificationType.APPLICATION_UPDATE_REQUIRED).subscribe(() => {
			this.showApplicationUpdate.set(true)
		})

		this.on<AccountDeletingNotification>(NotificationType.ACCOUNT_DELETING).subscribe((notification) => {
			this.accountDeletionEffectiveDate.set(notification.effectiveDate)
			this.showAccountDeletionModal.set(true)
		})

		this.on<TosAgreementRequiredNotification>(NotificationType.TOS_AGREEMENT_REQUIRED).subscribe(() => {
			this.cguInitial.set(true)
			this.showCguUpdate.set(true)
		})

		this.on<TosAgreementExpiredNotification>(NotificationType.TOS_AGREEMENT_EXPIRED).subscribe(() => {
			this.cguInitial.set(false)
			this.showCguUpdate.set(true)
		})

		this.on<DailyCreditNotification>(NotificationType.DAILY_CREDIT).subscribe((notification) => {
			this.dailyIncome.set({ tickets: notification.tickets })
			this.showDailyIncomeModal.set(true)
		})

		this.on<CurrencyTicketUpdateNotification>(NotificationType.CURRENCY_TICKET_UPDATE).subscribe((notification) => {
			this.authRepository.updatePlayerCurrencyTicket(notification.newAmount)
		})

		this.on<CurrencyMemoryKeyUpdateNotification>(NotificationType.CURRENCY_MEMORY_KEY_UPDATE).subscribe(
			(notification) => {
				this.authRepository.updatePlayerCurrencyMemoryKeys(notification.newAmount)
			},
		)

		this.on<NotEnoughTicketNotification>(NotificationType.NOT_ENOUGH_TICKET).subscribe(() => {
			if (!this.authRepository.player?.currencies.bankFreeTicketsCredited) {
				this.showBankruptcyGift.set(true)
				return
			}

			this.showBankruptcy.set(true)
		})

		this.on<NotEnoughKeyNotification>(NotificationType.NOT_ENOUGH_MEMORY_KEY).subscribe(() => {
			this.showBankruptcyKeys.set(true)
		})

		this.on<PlayerNotificationsUpdateNotification>(NotificationType.PLAYER_NOTIFICATIONS_UPDATE).subscribe(
			(notification) => {
				this.authRepository.updatePlayerNotifications(notification.notifications)
			},
		)

		this.on<SecretSceneEndedNotification>(NotificationType.SECRET_SCENE_ENDED).subscribe(() => {
			this.tutorialService.startTutorial(SECRET_SCENE_TUTORIAL)
		})
	}
}
